import { z } from 'zod';

// ── Sub-schemas ────────────────────────────────────────────────────────────────

export const EvidenceLinkSchema = z.object({
  url: z.string().url('Evidence link must be a valid URL'),
  label: z.string().min(1).max(200).trim(),
});

export const CapabilityProvenanceSchema = z.enum([
  'manual',
  'bulk_paste',
  'resume_import',
  'ai_assist',
]);

// ── Full capability (server → client) ─────────────────────────────────────────

export const CapabilitySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  experienceId: z.string().uuid(),
  body: z.string(),
  metrics: z.record(z.unknown()),
  skills: z.array(z.string()),
  evidenceLinks: z.array(EvidenceLinkSchema),
  provenance: CapabilityProvenanceSchema,
  sortOrder: z.number().int(),
  version: z.number().int(),
  embeddingVersion: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// ── Create request (client → server) ─────────────────────────────────────────

export const CreateCapabilitySchema = z.object({
  experienceId: z.string().uuid('Experience ID must be a valid UUID'),
  body: z.string().min(1, 'Bullet text is required').max(2000).trim(),
  skills: z.array(z.string().max(100).trim()).default([]),
  evidenceLinks: z.array(EvidenceLinkSchema).default([]),
  metrics: z.record(z.unknown()).default({}),
});

// ── Update request — content fields; experienceId allowed for drag-and-drop moves ──

export const UpdateCapabilitySchema = z.object({
  body: z.string().min(1).max(2000).trim().optional(),
  skills: z.array(z.string().max(100).trim()).optional(),
  evidenceLinks: z.array(EvidenceLinkSchema).optional(),
  metrics: z.record(z.unknown()).optional(),
  // Drag-and-drop fields — moving to a different experience or reordering within one
  experienceId: z.string().uuid().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

// ── Batch reorder (drag-and-drop) ────────────────────────────────────────────
// Used by POST /capabilities/reorder. Each entry may update sort_order and
// optionally move the bullet to a different experience (within the same tenure).

export const ReorderItemSchema = z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int().min(0),
  experienceId: z.string().uuid().optional(), // omit if not moving between experiences
});

export const ReorderCapabilitiesSchema = z.object({
  reorders: z.array(ReorderItemSchema).min(1),
});

// ── Bulk create (newline-delimited paste) ─────────────────────────────────────

export const BulkCreateCapabilitySchema = z.object({
  experienceId: z.string().uuid('Experience ID must be a valid UUID'),
  text: z.string().min(1, 'Paste text is required'),
  provenance: CapabilityProvenanceSchema.default('bulk_paste'),
});

// ── List query filters ────────────────────────────────────────────────────────

export const CapabilityFiltersSchema = z.object({
  experienceId: z.string().uuid().optional(),
  q: z.string().optional(),
  skill: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

// ── Derived types ─────────────────────────────────────────────────────────────

export type EvidenceLink = z.infer<typeof EvidenceLinkSchema>;
export type CapabilityProvenance = z.infer<typeof CapabilityProvenanceSchema>;
export type Capability = z.infer<typeof CapabilitySchema>;
export type CreateCapability = z.infer<typeof CreateCapabilitySchema>;
export type UpdateCapability = z.infer<typeof UpdateCapabilitySchema>;
export type ReorderItem = z.infer<typeof ReorderItemSchema>;
export type ReorderCapabilities = z.infer<typeof ReorderCapabilitiesSchema>;
export type BulkCreateCapability = z.infer<typeof BulkCreateCapabilitySchema>;
export type CapabilityFilters = z.infer<typeof CapabilityFiltersSchema>;
